/** Rubrique Importer : mode IA (Word/Excel/PDF/photo) et mode CSV (correspondance de colonnes). */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Upload, Sparkles, FileText, Check } from 'lucide-react';
import { toast } from 'sonner';
import { parametresApi, vignoblesApi } from '../../../services/api';
import { Button, Card, SegmentedControl, Spinner, Tag } from '../../../components/ui/kit';
import { PaysCombo, KNOWN_WINE_COUNTRIES } from '../../../components/wine/AppellationDrawer';

const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const MODES = [{ value: 'ia', label: 'Word · Excel · PDF avec IA' }, { value: 'csv', label: 'CSV · correspondance' }];
const FIELDS = { cuvee: 'Cuvée', domain: 'Domaine', year: 'Millésime', format: 'Format', color: 'Couleur', quantity: 'Quantité', location: 'Emplacement', purchase_price_ht: 'Prix d\'achat HT', price_table: 'Prix table', supplier: 'Fournisseur', supplier_ref: 'Réf. fournisseur', appellation: 'Appellation', cepages: 'Cépages', ignore: 'Ignorer' };

export default function Importer() {
  const [mode, setMode] = useState('ia');
  return (
    <div className="max-w-[920px]">
      <h2 className="font-title text-3xl mb-1">Importer</h2>
      <p className="text-[13px] text-n600 mb-4 max-w-[620px]">Reprenez votre existant : ancienne carte, fichier de stock, liste de domaines. Rien n'est écrit dans la cave avant votre validation.</p>
      <SegmentedControl options={MODES} value={mode} onChange={setMode} testId="import-mode" />
      <div className="mt-5">{mode === 'ia' ? <ImportIA /> : <ImportCSV />}</div>
    </div>
  );
}

/** Importer un index d'appellations (JSON ou CSV) — aperçu avant validation. Réservé au super-admin. */
export function AppellationIndexImport() {
  const [preview, setPreview] = useState(null);
  const [payload, setPayload] = useState(null); // { content, format }
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [defaultPays, setDefaultPays] = useState('France');

  const { data: existingPays = [] } = useQuery({ queryKey: ['appellation-pays'], queryFn: vignoblesApi.appellationPays });
  const paysOptions = [
    ...existingPays,
    ...KNOWN_WINE_COUNTRIES.filter((c) => !existingPays.some((p) => norm(p) === norm(c))),
  ];

  const runPreview = async (content, format, dp) => {
    setBusy(true);
    try {
      const r = await vignoblesApi.importAppellations(content, format, false, dp);
      setPreview(r); setPayload({ content, format });
    } catch (err) { toast.error(err.response?.data?.detail || 'Fichier illisible'); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const format = file.name.toLowerCase().endsWith('.csv') ? 'csv' : 'json';
    const content = await file.text();
    setPreview(null); setDone(null); setDefaultPays('France');
    await runPreview(content, format, 'France');
    e.target.value = '';
  };
  const changePays = async (v) => {
    setDefaultPays(v);
    if (payload) await runPreview(payload.content, payload.format, v || 'France');
  };
  const commit = async () => {
    setBusy(true);
    try {
      const r = await vignoblesApi.importAppellations(payload.content, payload.format, true, defaultPays || 'France');
      setDone(r); setPreview(null); setPayload(null);
      toast.success(`${r.imported_appellations} appellation(s) importée(s)`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Échec de l’import'); } finally { setBusy(false); }
  };

  const needsPays = preview && preview.has_pays === false;

  return (
    <Card className="p-4 mt-8" data-testid="import-appellations">
      <p className="font-title text-xl mb-1">Importer un index d'appellations</p>
      <p className="text-[12.5px] text-n600 mb-2">Format JSON (liste d'objets) ou CSV. Colonnes reconnues : <strong>appellation</strong> (requis), pays, vignoble, sous_region, couleur, cepages, remarques. Une ligne par couleur. Un aperçu s'affiche avant validation ; les appellations importées remplacent celles du même nom.</p>
      <label className="vd-btn vd-btn-secondary cursor-pointer" data-testid="appellations-import-upload">
        <Upload className="w-4 h-4" /> Déposer un fichier (.json / .csv)
        <input type="file" accept=".json,.csv,application/json,text/csv" className="hidden" onChange={onFile} />
      </label>
      {busy && <div className="mt-3"><Spinner /></div>}
      {done && <p className="mt-3 text-[13px] text-gold700" data-testid="appellations-import-done"><Check className="w-3.5 h-3.5 inline" /> {done.imported_appellations} appellation(s) · {done.imported_rows} ligne(s) importées.</p>}
      {preview && (
        <div className="mt-3" data-testid="appellations-import-preview">
          <p className="text-[13px] mb-2"><Check className="w-3.5 h-3.5 inline text-gold700" /> Aperçu : <strong>{preview.total_appellations}</strong> appellation(s), <strong>{preview.total_rows}</strong> ligne(s).</p>
          {needsPays && (
            <div className="vd-card p-3 mb-3 border-l-4 border-gold bg-surface/40" data-testid="appellations-import-pays">
              <p className="text-[13px] text-n700 mb-1.5">Ce fichier n'indique pas de <strong>pays</strong>. À quel pays associer ces appellations ?</p>
              <div className="w-56"><PaysCombo value={defaultPays} onChange={changePays} options={paysOptions} testId="appellations-import-pays-combo" /></div>
            </div>
          )}
          <div className="vd-card overflow-hidden max-h-[300px] overflow-y-auto mb-3">
            <table className="w-full text-[13px]"><thead className="sticky top-0 bg-paper"><tr className="text-left kicker"><th className="py-2 px-3">Appellation</th><th className="py-2 px-3">Pays</th><th className="py-2 px-3">Vignoble</th><th className="py-2 px-3">Couleurs</th></tr></thead>
              <tbody>
                {preview.preview.map((g, i) => (
                  <tr key={i} className="hair-t" data-testid={`appellations-preview-row-${i}`}>
                    <td className="py-2 px-3 font-medium">{g.appellation}</td>
                    <td className="py-2 px-3 text-n600">{g.pays}</td>
                    <td className="py-2 px-3 text-n600">{g.vignoble}{g.sous_region ? ` · ${g.sous_region}` : ''}</td>
                    <td className="py-2 px-3 text-n600">{g.entries.map((e) => e.couleur).filter(Boolean).join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <Button variant="primary" onClick={commit} disabled={busy} data-testid="appellations-import-commit">Importer {preview.total_appellations} appellation(s)</Button>
            <Button variant="ghost" onClick={() => { setPreview(null); setPayload(null); }} data-testid="appellations-import-cancel">Annuler</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function ImportIA() {
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);
  const [batch, setBatch] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setAnalysis(null); setBatch(null);
    try { setAnalysis(await parametresApi.importAnalyze(file)); }
    catch { toast.error('Échec de la lecture'); } finally { setBusy(false); }
  };
  const toggle = (id) => setAnalysis({ ...analysis, rows: analysis.rows.map((r) => r.id === id ? { ...r, include: !r.include } : r) });
  const commit = async () => {
    const r = await parametresApi.importCommit(analysis.rows.filter((x) => x.include));
    setBatch(r); toast.success(`${r.cuvees} cuvées importées`);
  };

  const included = analysis?.rows.filter((r) => r.include).length || 0;
  return (
    <div>
      <label className="vd-btn vd-btn-secondary cursor-pointer" data-testid="ia-upload"><Upload className="w-4 h-4" /> Déposer un fichier (Word, Excel, PDF, photo)<input type="file" accept=".docx,.xlsx,.pdf,image/*" className="hidden" onChange={onFile} /></label>
      {busy && <div className="mt-4"><Spinner label="Lecture par l'IA…" /></div>}
      {analysis && (
        <div className="mt-4">
          <p className="text-[13px] mb-2"><Check className="w-3.5 h-3.5 inline text-gold700" /> {analysis.filename} · Lu par l'IA</p>
          <div className="flex flex-wrap gap-4 text-[13px] mb-3" data-testid="ia-summary">
            <span>Domaines : <strong>{analysis.summary.domains}</strong></span>
            <span>Cuvées : <strong>{analysis.summary.cuvees}</strong></span>
            <span>Déjà connues : <strong>{analysis.summary.known}</strong></span>
            <span>À vérifier : <strong>{analysis.summary.to_check}</strong></span>
          </div>
          <p className="text-[12px] text-n500 mb-2">Les quantités restent à 0 : faites ensuite un inventaire guidé.</p>
          <div className="vd-card overflow-hidden max-h-[360px] overflow-y-auto">
            <table className="w-full text-[13px]" data-testid="ia-rows">
              <thead className="sticky top-0 bg-paper"><tr className="text-left kicker"><th className="py-2 px-3">Incl.</th><th className="py-2 px-3">Cuvée</th><th className="py-2 px-3">Millésimes</th><th className="py-2 px-3 text-right">Prix</th><th className="py-2 px-3">Action</th></tr></thead>
              <tbody>
                {analysis.rows.map((r) => (
                  <tr key={r.id} className={`hair-t ${r.include ? '' : 'opacity-40'}`} data-testid={`ia-row-${r.id}`}>
                    <td className="py-2 px-3"><input type="checkbox" checked={r.include} onChange={() => toggle(r.id)} className="accent-gold" /></td>
                    <td className="py-2 px-3">{r.name}<span className="block text-[11px] text-n600">{r.domain}</span></td>
                    <td className="py-2 px-3 nums text-n600">{(r.years || []).join(' · ') || '—'}</td>
                    <td className="py-2 px-3 text-right nums">{r.price ? `${r.price} €` : '—'}</td>
                    <td className="py-2 px-3"><Tag tone={r.action === 'Déjà en cave' ? 'neutral' : 'accent'}>{r.action}</Tag></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!batch ? (
            <Button variant="primary" className="mt-3" onClick={commit} disabled={!included} data-testid="ia-commit">Importer {included} cuvées</Button>
          ) : (
            <div className="mt-3 flex items-center gap-3">
              <span className="text-[13px] text-gold700">Import réalisé ({batch.cuvees} cuvées).</span>
              <Button variant="ghost" onClick={() => parametresApi.importUndo(batch.batch_id).then(() => { toast.success('Import annulé'); setBatch(null); setAnalysis(null); })} data-testid="ia-undo">Annuler l'import</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ImportCSV() {
  const [info, setInfo] = useState(null);
  const [file, setFile] = useState(null);
  const [mapping, setMapping] = useState({});
  const [onExists, setOnExists] = useState('update');
  const [qtyMode, setQtyMode] = useState('replace');
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setBusy(true); setReport(null);
    try { const d = await parametresApi.csvAnalyze(f); setInfo(d); setMapping(d.mapping || {}); }
    catch { toast.error('Échec analyse CSV'); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try { setReport(await parametresApi.csvCommit(file, mapping, onExists, qtyMode)); toast.success('Import CSV terminé'); }
    catch { toast.error('Échec import CSV'); } finally { setBusy(false); }
  };

  return (
    <div>
      <label className="vd-btn vd-btn-secondary cursor-pointer" data-testid="csv-upload"><Upload className="w-4 h-4" /> Déposer un CSV<input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} /></label>
      {busy && <div className="mt-4"><Spinner /></div>}
      {info && (
        <div className="mt-4">
          <p className="text-[13px] mb-3" data-testid="csv-info">{info.row_count} lignes · séparateur « {info.separator} » · {info.encoding}</p>
          <p className="kicker mb-2">Correspondance des colonnes</p>
          <div className="vd-card overflow-hidden mb-3">
            <table className="w-full text-[13px]"><thead><tr className="text-left kicker"><th className="py-2 px-3">Colonne</th><th className="py-2 px-3">Exemple</th><th className="py-2 px-3">Champ</th></tr></thead>
              <tbody>
                {info.headers.map((h, i) => {
                  const low = (info.confidence?.[h] ?? 1) < 0.6;
                  return (
                    <tr key={h} className="hair-t" data-testid={`csv-col-${i}`}>
                      <td className="py-2 px-3">{h}</td>
                      <td className="py-2 px-3 text-n600">{info.sample_rows?.[0]?.[i] || '—'}</td>
                      <td className="py-2 px-3">
                        <select className={`vd-input h-8 text-[12px] ${low ? 'border-gold' : ''}`} value={mapping[h] || 'ignore'} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })} data-testid={`csv-map-${i}`}>
                          {Object.entries(FIELDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-6 mb-3 text-[13px]">
            <label className="flex items-center gap-2">Si existe : <select className="vd-input h-8" value={onExists} onChange={(e) => setOnExists(e.target.value)}><option value="update">Mettre à jour</option><option value="ignore">Ignorer</option></select></label>
            <label className="flex items-center gap-2">Quantités : <select className="vd-input h-8" value={qtyMode} onChange={(e) => setQtyMode(e.target.value)}><option value="replace">Remplacer</option><option value="add">Ajouter</option></select></label>
          </div>
          <Button variant="primary" onClick={commit} disabled={busy} data-testid="csv-commit">Importer {info.row_count} lignes</Button>
          {report && (
            <div className="mt-4 text-[13px]" data-testid="csv-report">
              <p>Nouvelles références : <strong>{report.new_refs}</strong> · Mises à jour : <strong>{report.updated}</strong> · Erreurs : <strong>{report.errors.length}</strong></p>
              {report.errors.length > 0 && <ul className="mt-2 text-[12px] text-red-700 max-h-[120px] overflow-y-auto">{report.errors.slice(0, 20).map((e, i) => <li key={i}>Ligne {e.line} : {e.reason}</li>)}</ul>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
